import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import AuroraBg from "@/components/AuroraBg";
import { LaunchBanner } from "@/components/LaunchBanner";
import LeadMagnet from "@/components/LeadMagnet";
import Experiences from "@/components/Experiences";
import { ArrowRight, Sparkles, Moon, BookOpen, HeartHandshake } from "lucide-react";

const HERO_PORTRAIT =
  "/images/hero.jpg";

export default function Home() {
  usePageMeta(
    "Just be you · by Mehjabin — Manifestation, Meditation & Self-Discovery",
    "A sanctuary for becoming — e-books, guided meditations, tarot readings and 1:1 coaching with Mehjabin. You are not late. You are unfolding, exactly on time."
  );
  const [affirmation, setAffirmation] = useState(null);
  const [latestContent, setLatestContent] = useState([]);

  useEffect(() => {
    api.get("/affirmation/daily").then((r) => setAffirmation(r.data));
    api.get("/content").then((r) => setLatestContent(r.data.slice(0, 3)));
  }, []);

  return (
    <div data-testid="home-page">
      <LaunchBanner />
      {/* HERO */}
      <section className="relative min-h-[88vh] overflow-hidden">
        <AuroraBg />
        <div className="sacred-ring w-[700px] h-[700px] -right-40 top-32" />
        <div className="sacred-ring w-[500px] h-[500px] -left-32 -top-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: [0.2, 0.7, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="overline mb-6 flex items-center gap-3" data-testid="hero-overline">
              <Sparkles size={14} className="text-peach-deep" />
              A sanctuary for becoming
            </div>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] tracking-tight text-ink">
              You are not late.<br />
              You are <span className="shimmer-text not-italic font-medium italic">unfolding</span>,<br />
              <span className="text-lapis">exactly on time.</span>
            </h1>
            <p className="mt-10 max-w-xl text-lg text-ink/65 leading-relaxed">
              A bright, deliberate space for manifestation, meditation, and remembering who you've always been —
              held with care by Mehjabin.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/masterclass" className="btn-primary" data-testid="hero-masterclass-btn">
                The live masterclass <ArrowRight size={16} />
              </Link>
              <Link to="/bookings?service=tarot-oracle-reading" className="btn-soft" data-testid="hero-book-session-btn">
                Book a reading
              </Link>
            </div>
            <p className="mt-8 text-xs uppercase tracking-[0.22em] text-sage-deep/70 font-medium" data-testid="hero-trust-line">
              · Held with care across India, UAE &amp; beyond ·
            </p>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.2, 0.7, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -inset-6 bg-peach/30 organic-blob blur-2xl" />
            <div className="relative aspect-[3/4] max-h-[68vh] mx-auto max-w-[440px] overflow-hidden rounded-[2rem] shadow-2xl shadow-lapis/20">
              <img
                src={HERO_PORTRAIT}
                alt="Mehjabin"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "center top", transform: "scale(1.18)", transformOrigin: "center top" }}
                data-testid="hero-portrait"
              />
            </div>
            <div className="mt-6 flex justify-center">
              <div className="bg-white px-6 py-2.5 rounded-full shadow-md shadow-lapis/10 border border-lapis/10">
                <span className="font-serif italic text-lapis text-base">— Mehjabin</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRIMARY: MASTERCLASSES & WORKSHOPS */}
      <section className="relative py-20 md:py-28 bg-[#2a2118] overflow-hidden" data-testid="masterclass-strip-section">
        <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full border border-[#d4a574]/25" />
        <div className="absolute top-20 -right-4 w-[340px] h-[340px] rounded-full border border-[#d4a574]/15" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#d4a574] mb-6 flex items-center gap-2">
              <Sparkles size={12} /> Live Masterclasses &amp; Women's Transformational Workshops
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#f3ecdf] font-light leading-snug">
              Beyond Mindset — <span className="italic text-[#e0b184]">expanding the emotional capacity</span> of exhausted, high-achieving women.
            </h2>
            <p className="mt-6 text-[#f3ecdf]/70 max-w-2xl text-sm md:text-base leading-relaxed">
              A premium live masterclass blending modern somatic trauma-release with timeless, ancestral Vedic
              psychology — to safely dissolve burnout, clear intergenerational blockages, and restore your nervous
              system from the inside out. Live on Zoom, worldwide, replay included.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <Link to="/masterclass" className="inline-flex items-center gap-3 rounded-full bg-[#d4a574] hover:bg-[#e0b184] text-[#2a2118] px-8 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:-translate-y-0.5" data-testid="home-masterclass-cta">
                Explore the masterclass <ArrowRight size={16} />
              </Link>
              <span className="font-serif text-lg text-[#e0b184]">$47 USD · ₹3,900 INR</span>
            </div>
            <p className="mt-4 font-serif italic text-sm md:text-[15px] text-[#f3ecdf]/60 tracking-wide" data-testid="home-masterclass-microcopy">
              Live on Zoom. Dynamic worldwide timezone scheduler active at checkout. Replay included.
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="relative max-w-[320px] mx-auto">
              <div className="absolute -inset-3 rounded-[2rem] bg-[#d4a574]/20 blur-xl" />
              <Link to="/masterclass">
                <img src="/images/masterclass-hero.jpg" alt="Beyond Mindset masterclass" className="relative rounded-[2rem] border border-[#d4a574]/40 w-full object-cover aspect-[3/4]" data-testid="home-masterclass-image" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUIET READING LINE */}
      <section className="py-8 bg-cream/50 border-b border-lapis/10" data-testid="quiet-reading-line">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-ink/60">
          Prefer one-on-one? The same pattern keeps finding you?{" "}
          <Link to="/bookings?service=tarot-oracle-reading" className="text-lapis font-semibold border-b border-lapis/40 hover:border-lapis pb-0.5" data-testid="quiet-reading-link">
            Book a private reading · ₹3,000 →
          </Link>
        </div>
      </section>

      {/* AFFIRMATION */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="daily-affirmation-section">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] h-[480px] bg-lavender/40 organic-blob blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-6 inline-flex items-center gap-2 justify-center">
            <Sparkles size={12} /> Today's whisper
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink italic leading-tight font-light max-w-3xl mx-auto">
            "{affirmation?.title || "I am whole, I am safe, I am unfolding."}"
          </h2>
          {affirmation?.excerpt && (
            <p className="mt-8 text-ink/60 max-w-xl mx-auto">{affirmation.excerpt}</p>
          )}
          <div className="mt-10 w-16 h-[1px] bg-lapis/40 mx-auto" />
        </div>
      </section>

      {/* SACRED TOOLS & SHOP BAND */}
      <section className="py-16 md:py-20 max-w-7xl mx-auto px-6 md:px-12" data-testid="tools-band-section">
        <div className="rounded-[2.5rem] bg-gradient-to-br from-cream/60 via-peach/15 to-lavender/25 border border-lapis/10 px-8 md:px-14 py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <div className="overline mb-4">For the hands &amp; the heart</div>
            <h2 className="font-serif text-3xl md:text-4xl text-lapis font-light leading-tight">
              Sacred tools, gently curated.
            </h2>
            <p className="mt-3 text-sm text-ink/60 max-w-md">
              The Affirmation Deck and the Self-Discovery Journal — physical companions for the inner work.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <Link to="/sacred-tools" className="btn-primary" data-testid="home-sacred-tools-btn">
              Sacred Tools <ArrowRight size={16} />
            </Link>
            <Link to="/shop" className="btn-soft" data-testid="home-shop-quiet-btn">
              The full shop
            </Link>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-cream/40 via-peach/15 to-lavender/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-5">What we hold</div>
            <h2 className="font-serif text-4xl md:text-5xl text-lapis font-light leading-tight">
              Three quiet doors,<br />one inward path.
            </h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: Moon, title: "Manifestation", desc: "Practices that move from longing to belonging — softly, without urgency." },
              { icon: HeartHandshake, title: "Tarot Readings", desc: "A one-on-one reading that names the pattern — and what it's asking of you." },
              { icon: BookOpen, title: "Living Library", desc: "Meditations, articles, and teachings to return to, whenever you need." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="glass p-7 rounded-[2rem]" data-testid={`pillar-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <Icon size={28} className="text-lapis mb-5" strokeWidth={1.4} />
                <h3 className="font-serif text-2xl text-ink mb-3">{title}</h3>
                <p className="text-sm text-ink/65 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST LIBRARY */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="overline mb-5">From the library</div>
            <h2 className="font-serif text-4xl md:text-5xl text-lapis font-light leading-tight">
              Recent words<br />and meditations.
            </h2>
          </div>
          <Link to="/library" className="text-lapis text-sm tracking-[0.18em] uppercase border-b border-lapis/40 pb-1 hover:border-lapis w-fit font-semibold">
            Enter the library →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {latestContent.map((c) => (
            <Link key={c.id} to={`/library/${c.slug}`} className="group" data-testid={`home-content-${c.slug}`}>
              <div className="aspect-video overflow-hidden rounded-[1.5rem] mb-5 relative">
                <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />
                <div className="absolute top-4 left-4 overline bg-canvas/85 backdrop-blur px-3 py-1 rounded-full">{c.type} · {c.category}</div>
              </div>
              <h3 className="font-serif text-xl text-ink leading-tight group-hover:text-lapis transition-colors">{c.title}</h3>
              <p className="mt-2 text-sm text-ink/60">{c.excerpt}</p>
              {c.duration && <div className="mt-3 text-xs text-ink/50 uppercase tracking-widest">{c.duration}</div>}
            </Link>
          ))}
        </div>
      </section>

      {/* EXPERIENCES */}
      <Experiences />

      {/* CLOSING */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-peach/30 organic-blob blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="overline mb-6">An open door</div>
          <h2 className="font-serif text-4xl md:text-5xl text-ink italic font-light leading-tight">
            Whatever brought you here —<br />you are welcome, exactly as you are.
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/bookings" className="btn-primary" data-testid="closing-book-btn">Book a sitting</Link>
            <Link to="/about" className="btn-soft" data-testid="closing-about-btn">About Mehjabin</Link>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <LeadMagnet />
    </div>
  );
}
